import type { ToolCallChunkContent, ToolUseBlock } from '../../store/chatStore';
import { safeJsonParse } from '../../../../utils/jsonRepairUtils';

/**
 * Module-level cache for the last successfully parsed toolCallChunk args.
 *
 * Extracted into its own file to break a circular dependency:
 *   chatStore → MessageContent/index → child components → chatStore
 *
 * Both chatStore.ts (clearChunkCache) and MessageContent/index.tsx
 * (convertToolCallChunkToToolUse) can safely import from this file
 * without creating a cycle.
 */
const lastGoodInput = new Map<string, { [key: string]: unknown }>();

/** Clean up cache when a chunk is replaced by the final toolUse. */
export function clearChunkCache(chunkIndex?: number) {
  if (chunkIndex != null) {
    lastGoodInput.delete(`chunk-${chunkIndex}`);
  } else {
    lastGoodInput.clear();
  }
}

/**
 * Convert a streaming toolCallChunk into a toolUse-shaped object.
 *
 * - Stable ID (chunk.index) so React keys don't change between renders
 * - safeJsonParse (jsonrepair) for best-effort partial JSON parsing
 * - Always cache the latest parse — components filter out garbage themselves
 *   (e.g. WriteFilesBlock checks filePath is a valid string)
 * - On empty parse, reuse last cached value so components never flash empty
 */
export const convertToolCallChunkToToolUse = (
  chunk: ToolCallChunkContent,
): ToolUseBlock => {
  const stableId = `chunk-${chunk.index ?? 0}`;

  if (chunk.args) {
    const parsed = safeJsonParse<{ [key: string]: unknown }>(
      chunk.args,
      'tool chunk args',
    );
    if (Object.keys(parsed).length > 0) {
      lastGoodInput.set(stableId, parsed);
    }
  }

  return {
    type: 'toolUse',
    id: stableId,
    name: chunk.name || 'Unknown Tool',
    input: lastGoodInput.get(stableId) ?? {},
    meta: { isStreaming: true },
  };
};
